import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/shared/lib/supabase';
import { futebolRepository } from '../../data/futebolRepository';
import { obterOuCriarSessionId } from '@/shared/lib/jukeboxSession';
import type { EventoCraqueBagre, FutebolCraqueBagreVoto, FutebolEventoPartida } from '@/shared/types';

export type VotoCraqueBagre = 'concorda' | 'discorda';

export interface CraqueBagreState {
  dados:       EventoCraqueBagre | null;
  votado:      VotoCraqueBagre | null;
  concordas:   number;
  discordas:   number;
  totalVotos:  number;
  pctConcorda: number;
  votar:       (voto: VotoCraqueBagre) => void;
}

function isCraqueBagre(ev: FutebolEventoPartida): ev is EventoCraqueBagre {
  return ev !== null && (ev as EventoCraqueBagre).tipo === 'CRAQUE_BAGRE';
}

function naoExpirado(ev: EventoCraqueBagre): boolean {
  return new Date(ev.expiracao) > new Date();
}

function chaveIntervalo(ev: FutebolEventoPartida): string | null {
  if (!isCraqueBagre(ev) || !naoExpirado(ev)) return null;
  return ev.expiracao;
}

/**
 * useCraqueBagre
 *
 * - Detecta EventoCraqueBagre ativo via ultimo_evento (Realtime).
 * - Voto persistido em futebol_craque_bagre_votos via RPC
 *   `registrar_voto_craque_bagre` (SECURITY DEFINER): 1 voto por sessão por
 *   intervalo, valida fixture_id contra o jogo ativo NAQUELA tela — sem
 *   isso, um anônimo podia manipular o placar de outros tenants via
 *   broadcast direto no canal global `jogo:{fixtureId}`.
 * - Contagem sincronizada entre viewers via postgres_changes filtrado por
 *   tela_id.
 * - A chave do intervalo é `expiracao` — CDC de placar/pitch não zera os votos.
 */
export function useCraqueBagre(
  telaId: string | null,
  fixtureId: number | null,
  ultimoEvento: FutebolEventoPartida,
): CraqueBagreState {
  const [dados,     setDados]     = useState<EventoCraqueBagre | null>(null);
  const [votado,    setVotado]    = useState<VotoCraqueBagre | null>(null);
  const [concordas, setConcordas] = useState(0);
  const [discordas, setDiscordas] = useState(0);

  const expiracaoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const meuSessionIdRef   = useRef<string | null>(null);
  const geracaoRef        = useRef(0);
  const idsDoFetchRef     = useRef<Set<string>>(new Set());

  const chave = chaveIntervalo(ultimoEvento);

  useEffect(() => {
    if (!chave || !isCraqueBagre(ultimoEvento)) {
      setDados(null);
      return;
    }
    setDados(ultimoEvento);
    setConcordas(0);
    setDiscordas(0);
    setVotado(null);

    const ms = new Date(chave).getTime() - Date.now();
    if (expiracaoTimerRef.current) clearTimeout(expiracaoTimerRef.current);
    expiracaoTimerRef.current = setTimeout(() => setDados(null), Math.max(0, ms));

    return () => {
      if (expiracaoTimerRef.current) clearTimeout(expiracaoTimerRef.current);
    };
    // ultimoEvento omitido de propósito: só reabre o overlay quando a expiracao muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  // ── Fetch inicial + Realtime CDC dos votos deste intervalo ───────────────────
  useEffect(() => {
    if (!telaId || !fixtureId || !chave) return;

    meuSessionIdRef.current = obterOuCriarSessionId(telaId);

    let cancelado = false;
    const geracao = ++geracaoRef.current;
    const vistosRef = idsDoFetchRef;
    vistosRef.current = new Set();

    // Snapshot do servidor. Roda no mount e em todo `SUBSCRIBED` seguinte: o
    // heartbeat do worker religa o socket, mas não repõe o CDC perdido no
    // buraco — sem isso um INSERT perdido ficava fora da contagem até a
    // `expiracao` trocar.
    const hidratar = () => {
      futebolRepository
        .buscarVotosCraqueBagre(telaId, fixtureId, chave)
        .then((votos) => {
          if (cancelado || geracao !== geracaoRef.current) return;
          vistosRef.current = new Set(votos.map((v) => v.id));
          setConcordas(votos.filter((v) => v.voto === 'concorda').length);
          setDiscordas(votos.filter((v) => v.voto === 'discorda').length);
          const meu = votos.find((v) => v.session_id === meuSessionIdRef.current);
          if (meu) setVotado(meu.voto);
        })
        .catch(() => {});
    };

    hidratar();

    const aplicarVoto = (voto: FutebolCraqueBagreVoto) => {
      if (voto.expiracao !== chave) return;
      if (vistosRef.current.has(voto.id)) return;
      vistosRef.current.add(voto.id);
      if (voto.voto === 'concorda') setConcordas((n) => n + 1);
      if (voto.voto === 'discorda') setDiscordas((n) => n + 1);
      if (voto.session_id === meuSessionIdRef.current) setVotado(voto.voto);
    };

    const canal = supabase
      .channel(`craque-bagre-${telaId}-${fixtureId}`)
      .on(
        'postgres_changes' as never,
        {
          event: 'INSERT',
          schema: 'public',
          table: 'futebol_craque_bagre_votos',
          filter: `tela_id=eq.${telaId}`,
        } as never,
        (payload: { new: FutebolCraqueBagreVoto }) => aplicarVoto(payload.new),
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') hidratar();
      });

    return () => {
      cancelado = true;
      supabase.removeChannel(canal);
    };
  }, [telaId, fixtureId, chave]);

  const votar = useCallback((voto: VotoCraqueBagre) => {
    if (votado || !telaId || !fixtureId || !chave || !meuSessionIdRef.current) return;
    setVotado(voto);
    futebolRepository
      .registrarVotoCraqueBagre({
        tela_id:    telaId,
        fixture_id: fixtureId,
        expiracao:  chave,
        session_id: meuSessionIdRef.current,
        voto,
      })
      .catch(() => setVotado(null));
  }, [votado, telaId, fixtureId, chave]);

  const totalVotos  = concordas + discordas;
  const pctConcorda = totalVotos > 0 ? Math.round((concordas / totalVotos) * 100) : 50;

  return { dados, votado, concordas, discordas, totalVotos, pctConcorda, votar };
}
